import React from 'react';
import { Lien } from 'pages/Search/models';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
  Button,
  Descriptions,
  Typography,
  Row,
  Tooltip,
  Checkbox,
  Divider,
} from 'antd';
import {
  AttributeValuePair,
  formatDate,
  tabSelector,
  getDataTypeName,
} from 'common';
import {
  searchDetailsAdd,
  searchIsSelectedUpdate,
  isSelectedSelector,
} from 'pages/Search';
import { ResultTypeName } from 'pages/Search/Search.types';

const { Text } = Typography;

const selector = createSelector(
  tabSelector,
  isSelectedSelector,
  (tabName, getIsSelected) => ({
    tabName,
    getIsSelected,
  }),
);

const LiensListItem: React.FC<{
  searchId: number;
  lien: Lien;
  index: number;
  resultType: ResultTypeName;
  resultItemIndex: number;
}> = ({ index, lien, resultType, resultItemIndex }) => {
  const {
    remarks,
    indexNumber,
    controlNumber,
    debtor,
    creditor,
    docType,
    docketDate,
    block,
    attorney,
    lot,
    amount,
    guid,
    controlDate,
    satisfaction,
    county,
    documentType,
  } = lien;
  const dispatch = useDispatch();
  const { tabName, getIsSelected } = useSelector(selector);

  const onChange = ({ target: { checked } }: any) => {
    dispatch(
      searchIsSelectedUpdate(
        getDataTypeName(tabName),
        documentType,
        guid,
        checked,
        resultItemIndex,
        resultType,
      ),
    );
  };

  const isSelected = React.useMemo(() => {
    return getIsSelected(
      getDataTypeName(tabName),
      documentType,
      guid,
      resultItemIndex,
      resultType,
    );
  }, [documentType, getIsSelected, guid, resultItemIndex, resultType, tabName]);

  return (
    <Descriptions
      size="small"
      title={
        <Row type="flex" justify="space-between" align="middle">
          <Text>
            #{index + 1}&nbsp;
            <Tooltip title={'Check for export'}>
              <Checkbox onChange={onChange} checked={isSelected} />
            </Tooltip>
          </Text>
          <Button
            type="primary"
            size="small"
            icon="file-search"
            onClick={() => dispatch(searchDetailsAdd(lien))}
          >
            Details
          </Button>
        </Row>
      }
      column={5}
    >
      <Descriptions.Item>
        <AttributeValuePair attribute={'Debtor'} value={debtor.name} />
      </Descriptions.Item>
      <Descriptions.Item>
        <span className="break">
          <AttributeValuePair attribute={'Creditor'} value={creditor.name} />
        </span>
      </Descriptions.Item>
      <Descriptions.Item>
        <AttributeValuePair attribute={'Book'} value={docType} />
      </Descriptions.Item>
      <Descriptions.Item>
        <AttributeValuePair
          attribute={'Docket Date'}
          value={docketDate}
          valueType="date"
        />
      </Descriptions.Item>
      <Descriptions.Item>
        <AttributeValuePair
          attribute={'Control Number'}
          value={controlNumber}
        />
      </Descriptions.Item>
      <Descriptions.Item>
        <AttributeValuePair
          attribute={'Address'}
          value={debtor.address !== '' ? debtor.address : 'XXX'}
        />
      </Descriptions.Item>
      <Descriptions.Item>
        <AttributeValuePair attribute={'Attorney'} value={attorney.name} />
      </Descriptions.Item>
      <Descriptions.Item>
        <AttributeValuePair
          attribute={'Amount'}
          value={amount}
          valueType="money"
        />
      </Descriptions.Item>
      <Descriptions.Item>
        <AttributeValuePair
          attribute={'Satisfaction'}
          value={satisfaction !== '0' ? satisfaction : ''}
        />
      </Descriptions.Item>
      <Descriptions.Item>
        <AttributeValuePair
          attribute={'Satisfaction Date'}
          value={satisfaction !== '' && satisfaction !== 'N' ? controlDate : ''}
          valueType="date"
        />
      </Descriptions.Item>
      <Descriptions.Item span={3}>
        <AttributeValuePair attribute={'Block/Lot'} value={`${block}/${lot}`} />
      </Descriptions.Item>
      <Descriptions.Item span={2}>
        <AttributeValuePair attribute={'Index Number'} value={indexNumber} />
      </Descriptions.Item>
      <Descriptions.Item span={5}>
        <AttributeValuePair attribute={'County'} value={county} />
      </Descriptions.Item>
      <Descriptions.Item label="Remarks">
        <Divider dashed />
        {remarks && !!remarks.length ? (
          remarks.map((item, index) => (
            <React.Fragment key={index}>
              <AttributeValuePair
                attribute={`Date`}
                value={`${formatDate(item.remarkDate)} - ${item.remarkText}`}
              />
              <br />
            </React.Fragment>
          ))
        ) : (
          <Text>Remarks: N</Text>
        )}
      </Descriptions.Item>
    </Descriptions>
  );
};

export default React.memo(LiensListItem);
